import React, { useRef } from 'react';
import { Alert, Button, InputField, Modal } from 'src/shared/ui/elements';
import { Loading } from 'src/shared/ui/components';
import * as Yup from 'yup';
import { CheckboxField } from 'src/shared/ui/elements/index';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@apollo/client';
import { UPSERT_PAYMENT_METHOD } from '../graphql/upsert-payment-method.mutation';

import { handleApiError } from 'src/shared/utils/handleApiError';
import { ADD_ORDER_PAYMENT_METHOD_BY_GUEST } from '../graphql/add-order-payment-method-by-guest';
import { ACCOUNT_DATA } from 'src/account/my-account/graphql/account-data.query';
import { usePaymentMethodTokenizationConfigs } from 'src/shared/hooks/use-configs';
import { useAccountInfo } from 'src/account/shared/service/account.hooks';
import {
  PAYMENT_INTENT_SECRET,
  PAYMENT_METHOD_DATA,
} from '../graphql/payment-method-data.query';
import { ForwardCardField } from './components/fwd-card-field';
import { FORWARD_SDK_URLS } from 'src/shared/config';
import { useScript } from 'src/shared/hooks/use-script';
import { useLoading } from 'src/shared/hooks/use-loading';

const schema = Yup.object().shape({
  nameOnCard: Yup.string().label('Name on card').required(),
  cardData: Yup.object()
    .nullable()
    .typeError('Please enter a valid card number'),
  isPrimary: Yup.bool(),
});

export const PaymentMethodModal = ({
  onSuccess = () => {},
  initialValues,
  actions,
  orderId,
  orderAccountId,
}) => {
  const tokenizerRef = useRef();
  const onTokenizeRef = useRef();
  const { loading: fwdFieldLoadingState } = useLoading(true);

  const { loading: scriptLoading } = useScript(FORWARD_SDK_URLS[ENVIRONMENT]);

  const { data: accountData, loading: accountDataLoading } = useAccountInfo();
  const account = accountData?.account;
  const { id: accountId } = account ?? {};

  const { enablePineAppleTokenization, loading: configsLoading } =
    usePaymentMethodTokenizationConfigs();
  const { data: paymentIntentData, loading: paymentIntentLoading } = useQuery(
    PAYMENT_INTENT_SECRET,
    {
      variables: {
        accountId: Number(orderAccountId || accountId),
      },
      fetchPolicy: 'network-only',
      skip: !orderAccountId && !accountId,
    },
  );
  const paymentIntentSecret =
    paymentIntentData?.paymentIntentSecret?.paymentIntentSecret;

  const formMethods = useForm({
    defaultValues: {
      nameOnCard: initialValues?.nameOnCard ?? '',
      isPrimary: initialValues?.isPrimary ?? true,
      cardData: null,
    },
    resolver: yupResolver(schema),
    shouldUnregister: false,
  });
  const { register, handleSubmit, setError, formState } = formMethods;
  const { touchedFields, errors } = formState;

  const [mutate, { loading: mutationLoading }] = useMutation(
    orderId ? ADD_ORDER_PAYMENT_METHOD_BY_GUEST : UPSERT_PAYMENT_METHOD,
    {
      refetchQueries: [PAYMENT_METHOD_DATA, ACCOUNT_DATA],
      onError: (error) => {
        handleApiError(error, setError);
      },
      onCompleted: (result) => {
        if (result?.addOrderPaymentMethodByGuestUser?.paymentMethod) {
          onSuccess(result.addOrderPaymentMethodByGuestUser.paymentMethod);
        }
        if (result?.upsertPaymentMethod?.paymentMethod) {
          onSuccess(result.upsertPaymentMethod.paymentMethod);
        }
        actions.close();
      },
    },
  );

  const onSubmit = async (values) => {
    const { cardData, nameOnCard, isPrimary } = values;

    if (!cardData || !tokenizerRef.current) {
      setError('cardData', {
        type: 'custom',
        message: 'Could not validate credit card data',
      });
      return;
    }

    onTokenizeRef.current = async ({ forwardToken, pineappleToken }) => {
      if (enablePineAppleTokenization && !pineappleToken) {
        setError('cardData', {
          type: 'custom',
          message: 'Could not validate credit card data',
        });
        return;
      }

      const data = {
        nameOnCard,
        forwardToken,
        pineappleToken,
        isPrimary,
        ...cardData,
      };

      if (orderId) {
        data.orderId = orderId;
      }
      await mutate({
        variables: {
          input: data,
        },
      });
    };

    fwdFieldLoadingState.start();
    tokenizerRef.current({
      billingDetails: {
        address: {
          postalCode: cardData.zipcode,
        },
        name: nameOnCard,
      },
      ...(enablePineAppleTokenization
        ? {
            pineappleDetails: {
              authToken: PINEAPPLE_TOKEN,
              gatewayId: PINEAPPLE_MERCHANT,
            },
          }
        : {}),
    });
  };

  const isInitializing =
    scriptLoading ||
    accountDataLoading ||
    paymentIntentLoading ||
    configsLoading;

  const isLoading =
    isInitializing || mutationLoading || fwdFieldLoadingState.get;

  return (
    <Modal $title="Add credit card" $actions={actions}>
      <form
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(onSubmit)(event);
        }}
        id="payment-method-form"
      >
        <Modal.Content>
          {errors?.root?.serverError && (
            <Alert className="mb-16 level-error">
              {errors.root.serverError.message}
            </Alert>
          )}
          {errors?.cardData?.message && (
            <Alert className="mb-16 level-error">
              {errors.cardData.message}
            </Alert>
          )}

          {isInitializing ? (
            <Loading className="absolute" />
          ) : (
            <>
              <InputField
                {...register('nameOnCard')}
                $label="Name on card"
                className="tw-mb-3"
                isTouched={touchedFields?.nameOnCard}
                error={errors?.nameOnCard?.message}
              />

              <FormProvider {...formMethods}>
                <ForwardCardField
                  tokenizerRef={tokenizerRef}
                  onTokenizeRef={onTokenizeRef}
                  paymentIntentSecret={paymentIntentSecret}
                  enablePineAppleTokenization={enablePineAppleTokenization}
                  fwdFieldLoadingState={fwdFieldLoadingState}
                />
              </FormProvider>
            </>
          )}
        </Modal.Content>

        <div className="px-16 mb-12">
          <CheckboxField
            {...register('isPrimary')}
            $label="Set this payment method as the default"
            labelClassName="fs-14 fw-500"
          />
        </div>

        {isLoading && <Loading className="absolute" />}

        <Modal.Footer className="flex align-center justify-flex-end">
          <Button className="alt outline" onClick={actions.close} type="button">
            Cancel
          </Button>

          <Button
            className="ml-12"
            type="submit"
            form="payment-method-form"
            $loading={isLoading}
            $loadingText="Saving..."
          >
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
